import { useState } from 'react'
import { useUserData } from '../lib/useUserData.js'
import { calculate, DOSE_UNITS } from '../lib/peptide.js'
import { toKey, addDays, fromKey, todayKey } from '../lib/dates.js'
import Modal from './Modal.jsx'

const FREQS = [
  { id: 1, label: 'Every day' },
  { id: 2, label: 'Every 2 days' },
  { id: 3, label: 'Every 3 days' },
  { id: 7, label: 'Weekly' },
]

/*
 * Schedule a saved calculation (or an ad-hoc dose) across the calendar.
 * `calc` provides the peptide/dose details; user picks start, frequency, count.
 */
export default function ScheduleModal({ calc, onClose, onScheduled, onError, defaultDate }) {
  const { addEvents, events, deleteEventsByCalc, notifySettings, updateNotifySettings } = useUserData()
  const [start, setStart] = useState(defaultDate || todayKey())
  const [every, setEvery] = useState(1)
  const [count, setCount] = useState(7)
  const [time, setTime] = useState('08:00')
  // Reminder opt-in, right where doses are scheduled — so people actually find it
  // (it was buried in Settings). Defaults to their existing preference, or ON for
  // anyone who hasn't set one. Controls the global dose_reminders toggle.
  const [remind, setRemind] = useState(notifySettings?.dose_reminders ?? true)

  const unitLabel = DOSE_UNITS.find((u) => u.id === calc.doseUnit)?.label || 'mcg'
  const res = calculate(calc)

  // Doses already on the calendar for this saved calc that haven't been taken.
  const existing = calc.id ? events.filter((e) => e.calcId === calc.id && !e.taken) : []
  const [replace, setReplace] = useState(true)
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    const n = Math.max(1, Math.min(365, Number(count) || 1))
    const startDate = fromKey(start)
    const newEvents = Array.from({ length: n }, (_, i) => ({
      date: toKey(addDays(startDate, i * every)),
      time,
      calcId: calc.id || null,
      name: calc.name || 'Peptide dose',
      dose: calc.dose,
      doseUnit: calc.doseUnit,
      units: res.ok ? res.units : null,
    }))
    setBusy(true)
    // Clear the old schedule first when replacing, so editing frequency doesn't stack.
    if (replace && existing.length > 0) {
      const del = await deleteEventsByCalc(calc.id)
      if (del?.error) {
        setBusy(false)
        return onError?.(del.error)
      }
    }
    const result = await addEvents(newEvents)
    if (result?.error) {
      setBusy(false)
      return onError?.(result.error)
    }
    // Persist the reminder preference (global toggle) if it changed or was never set.
    // Non-fatal: the doses are already on the calendar even if this write fails.
    if (remind !== (notifySettings?.dose_reminders ?? null)) {
      await updateNotifySettings({ dose_reminders: remind })
    }
    setBusy(false)
    onScheduled?.(newEvents.length)
  }

  return (
    <Modal onClose={onClose} title={`Schedule — ${calc.name || 'dose'}`}>
      <div className="modal-body">
        <div className="pill blue" style={{ marginBottom: 16 }}>
          {calc.dose} {unitLabel} · {res.ok ? `${res.units} units` : '—'} each
        </div>

        <div className="field">
          <label className="label">Start date</label>
          <input className="input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>

        <div className="grid-2">
          <div className="field">
            <label className="label">Frequency</label>
            <select className="select" value={every} onChange={(e) => setEvery(Number(e.target.value))}>
              {FREQS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Time</label>
            <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </div>
        </div>

        <div className="field mb-0">
          <label className="label">Number of doses</label>
          <input className="input" type="number" min="1" max="365" value={count}
            onChange={(e) => setCount(e.target.value)} />
          <p className="muted small" style={{ marginTop: 6, marginBottom: 0 }}>
            Ends {new Date(fromKey(start).getTime() + (Math.max(1, Number(count) || 1) - 1) * every * 86400000)
              .toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {existing.length > 0 && (
          <label className="consent-row" style={{ marginTop: 16 }}>
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
            <span>
              Replace the <strong>{existing.length}</strong> dose{existing.length > 1 ? 's' : ''} already
              scheduled for this calculation <span className="muted">(taken doses are kept)</span>.
            </span>
          </label>
        )}

        <label className="consent-row" style={{ marginTop: 16 }}>
          <input type="checkbox" checked={remind} onChange={(e) => setRemind(e.target.checked)} />
          <span>
            📧 Email me a reminder before each dose{' '}
            <span className="muted">(the day before — you can change or turn this off anytime in Settings).</span>
          </span>
        </label>
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Adding…' : 'Add to calendar'}
        </button>
      </div>
    </Modal>
  )
}
